import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar, Clock, ChevronLeft, ChevronRight, X, CalendarPlus, Check, Loader2, ListChecks, CheckSquare, Timer } from 'lucide-react'
import { useCardHistory } from '../hooks/useCardHistory'

const FIELD_LABEL_KEYS = {
    title: 'cardDateHistory.fields.title',
    description: 'cardDateHistory.fields.description',
    list_id: 'cardDateHistory.fields.list',
    is_completed: 'cardDateHistory.fields.completed',
    priority: 'cardDateHistory.fields.priority',
    status: 'cardDateHistory.fields.status',
    due_date: 'cardDateHistory.fields.dueDate',
    created: 'cardDateHistory.fields.created',
    current_state: 'cardDateHistory.fields.currentState',
    checklist_completed: 'cardDateHistory.fields.checklistCompleted',
    completion_payload: 'cardDateHistory.fields.completionPayload',
}

const EVENT_ICONS = {
    snapshot: Calendar,
    checklist_item: ListChecks,
    card_done: CheckSquare,
    time_log: Timer,
    card_recurring: Clock,
}

const EVENT_COLORS = {
    snapshot: 'var(--accent-purple)',
    checklist_item: '#60a5fa',
    card_done: '#22c55e',
    time_log: '#a78bfa',
    card_recurring: '#f59e0b',
}

function browserLocale(language) {
    if (String(language || '').startsWith('zh')) return 'zh-CN'
    if (String(language || '').startsWith('en')) return 'en-GB'
    return 'ro-RO'
}

function fieldLabel(field, t) {
    return FIELD_LABEL_KEYS[field] ? t(FIELD_LABEL_KEYS[field]) : field
}

function formatTime(dateStr, language) {
    const d = new Date(dateStr)
    return d.toLocaleTimeString(browserLocale(language), { hour: '2-digit', minute: '2-digit' })
}

function formatDate(dateStr, language) {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString(browserLocale(language), {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    })
}

function formatDuration(minutes, t) {
    if (!minutes || minutes <= 0) return null
    if (minutes < 60) return t('cardDateHistory.durationMinutes', { count: minutes })
    return t('cardDateHistory.durationHours', {
        hours: Math.floor(minutes / 60),
        minutes: minutes % 60,
    })
}

function getEventLabel(entry, t) {
    switch (entry.type) {
        case 'checklist_item': return t('cardDateHistory.events.checklistItem')
        case 'card_done': return t('cardDateHistory.events.cardDone')
        case 'time_log': return t('cardDateHistory.events.timeLog')
        case 'card_recurring': return t('cardDateHistory.events.cardRecurring')
        case 'snapshot': {
            const fields = entry.changed_fields || []
            if (fields.includes('created')) return t('cardHistory.actions.created')
            if (fields.includes('current_state')) return t('cardDateHistory.events.stateSaved')
            if (fields.includes('checklist_completed')) return t('cardDateHistory.fields.checklistCompleted')
            // Prefer the rich human-readable line ("Creat de X",
            // "Comentariu: …", "Mutat în «…»") as the headline instead of
            // the generic "Modificare".
            return entry.detail_text || fields.map(f => fieldLabel(f, t)).join(', ') || t('cardHistory.actions.modified')
        }
        default: return t('cardDateHistory.events.generic')
    }
}

export default function CardDateHistory({ cardId, userId, isAdmin }) {
    const { t, i18n } = useTranslation()
    const {
        timeline,
        scheduledChanges,
        availableDates,
        loading,
        getEntriesForDate,
        scheduleChange,
        cancelScheduledChange,
    } = useCardHistory(cardId)

    const [selectedDate, setSelectedDate] = useState(null)
    const [showScheduleForm, setShowScheduleForm] = useState(false)
    const [scheduleDate, setScheduleDate] = useState('')
    const [scheduleField, setScheduleField] = useState('title')
    const [scheduleValue, setScheduleValue] = useState('')
    const [scheduling, setScheduling] = useState(false)

    const activeDate = selectedDate || availableDates[0] || null
    const dateEntries = useMemo(() => {
        if (!activeDate) return []
        return getEntriesForDate(activeDate)
    }, [activeDate, getEntriesForDate])

    const dateIndex = availableDates.indexOf(activeDate)
    const canGoBack = dateIndex < availableDates.length - 1
    const canGoForward = dateIndex > 0

    const handleSchedule = async () => {
        if (!scheduleDate || !scheduleField || !scheduleValue) return
        setScheduling(true)
        await scheduleChange(scheduleDate, { [scheduleField]: scheduleValue }, userId)
        setScheduling(false)
        setShowScheduleForm(false)
        setScheduleDate('')
        setScheduleValue('')
    }

    const tomorrow = useMemo(() => {
        const d = new Date()
        d.setDate(d.getDate() + 1)
        return d.toISOString().split('T')[0]
    }, [])

    const pendingChanges = scheduledChanges.filter((c) => c.status === 'pending')

    if (loading && timeline.length === 0) {
        return <div className="cdh-loading"><Loader2 size={18} className="gsearch-spinner" /> {t('cardDateHistory.loading')}</div>
    }

    return (
        <div className="cdh-container">
            {/* Date navigator */}
            <div className="cdh-nav">
                <button className="cdh-nav-btn" disabled={!canGoBack}
                    onClick={() => setSelectedDate(availableDates[dateIndex + 1])} title={t('cardDateHistory.previousDay')}>
                    <ChevronLeft size={16} />
                </button>
                <div className="cdh-nav-date">
                    <Calendar size={14} />
                    {activeDate ? formatDate(activeDate, i18n.language) : t('cardDateHistory.noHistory')}
                </div>
                <button className="cdh-nav-btn" disabled={!canGoForward}
                    onClick={() => setSelectedDate(availableDates[dateIndex - 1])} title={t('cardDateHistory.nextDay')}>
                    <ChevronRight size={16} />
                </button>
            </div>

            {/* Summary for the day */}
            {activeDate && dateEntries.length > 0 && (
                <div className="cdh-day-summary">
                    {(() => {
                        const checklistCount = dateEntries.filter(e => e.type === 'checklist_item').length
                        const snapshotCount = dateEntries.filter(e => e.type === 'snapshot').length
                        const cardDone = dateEntries.some(e => e.type === 'card_done')
                        const parts = []
                        if (checklistCount > 0) parts.push(t('cardDateHistory.summary.steps', { count: checklistCount }))
                        if (snapshotCount > 0) parts.push(t('cardDateHistory.summary.changes', { count: snapshotCount }))
                        if (cardDone) parts.push(t('cardDateHistory.events.cardDone'))
                        return parts.join(' · ')
                    })()}
                </div>
            )}

            {/* Timeline */}
            {activeDate && dateEntries.length > 0 ? (
                <div className="cdh-timeline">
                    {dateEntries.map((entry) => {
                        const Icon = EVENT_ICONS[entry.type] || Calendar
                        const color = EVENT_COLORS[entry.type] || 'var(--text-secondary)'
                        return (
                            <div key={entry.id} className="cdh-entry">
                                <div className="cdh-entry-icon" style={{ color }}>
                                    <Icon size={14} />
                                </div>
                                <div className="cdh-entry-content">
                                    <div className="cdh-entry-header">
                                        <span className="cdh-entry-label">{getEventLabel(entry, t)}</span>
                                        <span className="cdh-entry-time">{formatTime(entry.timestamp, i18n.language)}</span>
                                    </div>
                                    {entry.detail_text && entry.detail_text !== getEventLabel(entry, t) && (
                                        <div className="cdh-entry-detail">{entry.detail_text}</div>
                                    )}
                                    {entry.duration_minutes > 0 && (
                                        <div className="cdh-entry-detail">{formatDuration(entry.duration_minutes, t)}</div>
                                    )}
                                    {entry.user_name && (
                                        <span className="cdh-entry-author">{entry.user_name}</span>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            ) : activeDate ? (
                <div className="cdh-empty">{t('cardDateHistory.emptyDay')}</div>
            ) : null}

            {/* Scheduled changes */}
            {pendingChanges.length > 0 && (
                <div className="cdh-scheduled">
                    <div className="cdh-scheduled-title">
                        <CalendarPlus size={14} /> {t('cardDateHistory.scheduledTitle')}
                    </div>
                    {pendingChanges.map((change) => (
                        <div key={change.id} className="cdh-scheduled-item">
                            <div className="cdh-scheduled-date">{formatDate(change.effective_date, i18n.language)}</div>
                            <div className="cdh-scheduled-fields">
                                {Object.entries(change.changes || {}).map(([key, val]) => (
                                    <span key={key} className="cdh-scheduled-field">
                                        {fieldLabel(key, t)}: <strong>{String(val)}</strong>
                                    </span>
                                ))}
                            </div>
                            {isAdmin && (
                                <button className="cdh-cancel-btn" onClick={() => cancelScheduledChange(change.id)} title={t('common.cancel')}>
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Schedule form (admin) */}
            {isAdmin && (
                <div className="cdh-schedule-section">
                    {!showScheduleForm ? (
                        <button className="cdh-schedule-btn" onClick={() => setShowScheduleForm(true)}>
                            <CalendarPlus size={14} /> {t('cardDateHistory.scheduleChange')}
                        </button>
                    ) : (
                        <div className="cdh-schedule-form">
                            <div className="cdh-schedule-form-title">{t('cardDateHistory.scheduledChange')}</div>
                            <label className="cdh-label">
                                {t('cardDateHistory.applyDate')}
                                <input type="date" className="cdh-date-input" value={scheduleDate}
                                    onChange={(e) => setScheduleDate(e.target.value)} min={tomorrow} />
                            </label>
                            <label className="cdh-label">
                                {t('cardDateHistory.field')}
                                <select className="cdh-select" value={scheduleField}
                                    onChange={(e) => setScheduleField(e.target.value)}>
                                    <option value="title">{t('cardDateHistory.fields.title')}</option>
                                    <option value="description">{t('cardDateHistory.fields.description')}</option>
                                    <option value="priority">{t('cardDateHistory.fields.priority')}</option>
                                    <option value="status">{t('cardDateHistory.fields.status')}</option>
                                </select>
                            </label>
                            <label className="cdh-label">
                                {t('cardDateHistory.newValue')}
                                <input type="text" className="cdh-text-input" value={scheduleValue}
                                    onChange={(e) => setScheduleValue(e.target.value)} placeholder={t('cardDateHistory.valuePlaceholder')} />
                            </label>
                            <div className="cdh-schedule-actions">
                                <button className="cdh-confirm-btn" onClick={handleSchedule}
                                    disabled={scheduling || !scheduleDate || !scheduleValue}>
                                    {scheduling ? <Loader2 size={14} className="gsearch-spinner" /> : <Check size={14} />}
                                    {t('cardDateHistory.schedule')}
                                </button>
                                <button className="cdh-cancel-form-btn" onClick={() => setShowScheduleForm(false)}>{t('common.cancel')}</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
